import { LABEL, PACKING_SLIP, EOD_FORM, ORDER_SUMMARY, PICK_LIST, SHIPMENT_MANIFEST } from '../../enums/document-type';

/**
 * @typedef {object} NavigationCommand
 * @property {string} route - The route path
 * @property {string} title - Display title
 * @property {string} description - Description for semantic matching
 * @property {string} category - Category grouping
 * @property {string[]} [parameters] - Optional route parameters
 * @property {string} [highlightSelector] - Optional CSS selector to highlight
 * @property {string} [fallbackRoute] - Fallback route if parameters are missing
 * @property {{ paramKey: string, target: string }} [prefillConfig] - If set, the handler extracts commandParams[paramKey] and dispatches it as pendingPrefill with the given target
 */

/**
 * @typedef {object} NavigationManifest
 * @property {string} version - Manifest version
 * @property {Record<string, NavigationCommand>} commands - Available commands
 */

/**
 * Navigation manifest defining all available commands
 * Sent to backend with every request for command routing
 * Note: No keywords - Claude uses semantic matching natively
 * @type {NavigationManifest}
 */
export const navigationManifest = {
  version: '0.1',
  commands: {
    // Settings Commands
    'settings.printing': {
      route: '/settings/printing',
      title: 'Printing Settings',
      description: 'Configure printers, document formats, and print rules for all possible documents. These are global settings.',
      category: 'Settings',
    },

    'settings.printing.label': {
      route: '/settings/printing',
      title: 'Printing Settings for Labels',
      description: 'Configure printers, document formats, and print rules for all label documents. These are global settings.',
      category: 'Settings',
      highlightSelector: `[data-shippy-highlight-id="${LABEL}"]`,
    },

    'settings.printing.packingSlip': {
      route: '/settings/printing',
      title: 'Packing Slip Printing Setup',
      description:
        'Configure how packing slips print: format/size (8.5x11, 2 per page), sort items by quantity, show/hide content like store logo, item prices, SKUs, barcodes, locations, discounts, and zero quantity items. These are printing preferences, NOT template customization.',
      category: 'Settings',
      highlightSelector: `[data-shippy-highlight-id="${PACKING_SLIP}"]`,
    },

    'settings.printing.endOfDayForm': {
      route: '/settings/printing',
      title: 'Printing Settings for End of Day Forms',
      description: 'Configure printers, document formats, and print rules for all end of day form documents. These are global settings.',
      category: 'Settings',
      highlightSelector: `[data-shippy-highlight-id="${EOD_FORM}"]`,
    },

    'settings.printing.orderSummary': {
      route: '/settings/printing',
      title: 'Printing Settings for Order Summary',
      description: 'Configure printers, document formats, and print rules for all order summary documents. These are global settings.',
      category: 'Settings',
      highlightSelector: `[data-shippy-highlight-id="${ORDER_SUMMARY}"]`,
    },

    'settings.printing.pickList': {
      route: '/settings/printing',
      title: 'Printing Settings for Pick Lists',
      description: 'Configure printers, document formats, and print rules for all pick list documents. These are global settings.',
      category: 'Settings',
      highlightSelector: `[data-shippy-highlight-id="${PICK_LIST}"]`,
    },

    'settings.printing.shipmentManifest': {
      route: '/settings/printing',
      title: 'Printing Settings for Shipment Manifests',
      description: 'Configure printers, document formats, and print rules for all shipment manifest documents. These are global settings.',
      category: 'Settings',
      highlightSelector: `[data-shippy-highlight-id="${SHIPMENT_MANIFEST}"]`,
    },

    'settings.stores.add': {
      route: '/settings/stores',
      title: 'Add Store',
      description: 'Connect a new sales channel or marketplace to ShipStation',
      category: 'Settings',
      highlightSelector: '[data-shippy-highlight-id="add-store"]',
    },

    'settings.stores.manage': {
      route: '/settings/stores/:storeGuid/storesettings',
      title: 'Store Settings',
      description: 'Configure settings for a specific connected store',
      category: 'Settings',
      parameters: ['storeGuid'],
      fallbackRoute: '/settings/stores',
    },

    'settings.stores.servicemappings': {
      route: '/settings/stores/:storeGuid/shippingservices',
      title: 'Service Mappings',
      description:
        'Configure service mappings for a store. Maps shipping services sent by a marketplace to shipping services in ShipStation. Use this when a user asks about service mappings, shipping service mappings, or mapping marketplace services to carriers.',
      category: 'Settings',
      parameters: ['storeGuid'],
      fallbackRoute: '/settings/stores',
    },

    'settings.automation': {
      route: '/settings/automationrules',
      title: 'Automation Rules',
      description: 'Create and manage automation rules for orders, tags, and shipping',
      category: 'Settings',
    },

    'settings.smartrouting': {
      route: '/settings/smartroutingrules',
      title: 'Smart Routing',
      description:
        'Configure rules that select which ship from location ships an order based on distance to the customer or inventory availability. This is specifically about ship from location selection, NOT general order automation. If the user asks about automation generally or about automating other aspects of their workflow, use settings.automation instead.',
      category: 'Settings',
    },

    'settings.carriers': {
      route: '/settings/carriers',
      title: 'Carriers',
      description:
        'Connect and configure shipping carriers like USPS, FedEx, UPS. Also view or add funds to your ShipStation balance, wallet balance, or shipping balance. Manage your prepaid shipping account funds used for purchasing postage.',
      category: 'Settings',
    },

    'settings.warehouses': {
      route: '/settings/warehouses',
      title: 'Warehouses',
      description:
        'View and manage ship from locations and shipping origins. These are shipping origin locations & return addresses, NOT inventory storage locations. Use this when the user wants to SEE their locations, NOT when they want to create or edit one.',
      category: 'Settings',
    },

    'settings.warehouses.create': {
      route: '/settings/warehouses?openCreateShipFrom=true&source=shippy',
      title: 'Add Ship From Location',
      description:
        'Add, create, or set up a new ship from location (warehouse / origin address) in Settings. This is NOT about automation rules — it opens the ship from form where the user enters a physical address. If the user provides address details, parse them into the address parameter using the shipFromAddressFields schema. Opens the form pre-filled with any provided address.',
      category: 'Settings',
      prefillConfig: { paramKey: 'address', target: 'ship-from' },
    },

    'settings.warehouses.edit': {
      route: '/settings/warehouses?editShipFromId=:shipFromId&source=shippy',
      title: 'Edit Ship From Location',
      description:
        'Edit an existing ship from location or warehouse address. Provide the shipFromId from the shipFromLocations context as a parameter. If the user provides new address details, parse them into the address parameter using the shipFromAddressFields schema. Opens the edit form with changes pre-filled.',
      category: 'Settings',
      parameters: ['shipFromId'],
      fallbackRoute: '/settings/warehouses',
      prefillConfig: { paramKey: 'address', target: 'ship-from' },
    },

    'settings.users': {
      route: '/settings/users',
      title: 'Users',
      description: 'Manage user accounts and permissions',
      category: 'Settings',
    },

    'settings.api': {
      route: '/settings/api',
      title: 'API Keys',
      description: 'Generate and manage API keys for integrations',
      category: 'Settings',
    },

    'settings.packingslips': {
      route: '/settings/packingslips',
      title: 'Packing Slip Templates (HTML/CSS Customization)',
      description:
        'Create and edit custom packing slip templates using HTML/CSS. Customize the visual design, layout, and branding of packing slips. This is for advanced template customization, NOT printing preferences like format or what content to show.',
      category: 'Settings',
    },

    'settings.myprofile': {
      route: '/settings/myprofile',
      title: 'My Profile',
      description: 'Manage your personal profile settings and preferences',
      category: 'Settings',
    },

    'settings.signin-security': {
      route: '/settings/signin-security',
      title: 'Sign In & Security',
      description: 'Manage authentication, password, and security settings',
      category: 'Settings',
    },

    'settings.displayoptions': {
      route: '/settings/displayoptions',
      title: 'Display Options',
      description: 'Configure UI display preferences: date/time format, seller timezone, or weight and dimension units.',
      category: 'Settings',
    },

    'settings.payments': {
      route: '/settings/payments',
      title: 'Payment & Subscription',
      description:
        'View and manage billing information, subscription details, and payment methods including ACH and credit cards. Use for payment method management, adding billing info, or viewing account balance. NOT for changing subscription plans — use settings.payments.manageSubscription for that.',
      category: 'Settings',
    },

    'settings.payments.manageSubscription': {
      route: '/settings/payments?openManageSubscription=true&source=shippy',
      title: 'Change Subscription Plan',
      description:
        'Change, upgrade, downgrade, or modify the current subscription plan. Opens the plan selection drawer where the user can compare and choose a new plan.',
      category: 'Settings',
    },

    'settings.senderemails': {
      route: '/settings/senderemails',
      title: 'Sender Emails',
      description: 'Configure sender email addresses for notifications',
      category: 'Settings',
    },

    'settings.workflow': {
      route: '/settings/workflow',
      title: 'Shipping Workflow',
      description:
        'Configure the default create label button behavior, QuickShip (one-click shipping), and scan-to-print verification settings. Do NOT use for automation rules — use settings.automation for that.',
      category: 'Settings',
    },

    'settings.filters': {
      route: '/settings/filters',
      title: 'Filters',
      description: 'Create and manage order filters',
      category: 'Settings',
    },

    'settings.smartfillrules': {
      route: '/settings/smartfillrules',
      title: 'Smart Fill Rules',
      description:
        'Configure SmartFill rules that automatically populate shipment fields (weight, dimensions, package) based on past shipment history. Only use this if the user specifically asks about SmartFill. If the user asks about automation use settings.automation instead.',
      category: 'Settings',
    },

    'settings.fulfillment': {
      route: '/settings/fulfillment',
      title: 'Fulfillment Providers',
      description: 'Connect and manage fulfillment service providers',
      category: 'Settings',
    },

    'settings.insurance': {
      route: '/settings/insurance',
      title: 'Insurance Settings',
      description: 'Configure shipping insurance options and providers',
      category: 'Settings',
    },

    'settings.packages': {
      route: '/settings/packages',
      title: 'Package Settings',
      description: 'Define package sizes, weights, and dimensions',
      category: 'Settings',
    },

    'settings.returns': {
      route: '/settings/returns',
      title: 'Returns Settings',
      description: 'Configure return label and process settings',
      category: 'Settings',
    },

    'settings.international': {
      route: '/settings/international',
      title: 'International Settings',
      description: 'Configure customs and international shipping settings',
      category: 'Settings',
    },

    'settings.connect': {
      route: '/settings/connect',
      title: 'ShipStation Connect',
      description:
        'ShipStation Connect is a downloadable desktop printing application. Also referred to as the print driver, desktop app, or local printing client. Configure settings, download the application, or troubleshoot local printing issues.',
      category: 'Settings',
    },

    'settings.emailtemplates': {
      route: '/settings/emailtemplates',
      title: 'Email Templates',
      description: 'Customize shipment confirmation and delivery notification email templates',
      category: 'Settings',
    },

    'settings.picklist': {
      route: '/settings/picklist',
      title: 'Pick List Settings',
      description: 'Configure pick list templates and settings',
      category: 'Settings',
    },

    'settings.inventorysettings': {
      route: '/settings/inventorysettings',
      title: 'Inventory Settings',
      description: 'Configure inventory management settings',
      category: 'Settings',
    },

    'settings.allocationstrategy': {
      route: '/settings/allocationstrategy',
      title: 'Allocation Strategy',
      description:
        'Configure how inventory is allocated to orders by priority. Create and manage allocation tasks that control which orders get inventory first.',
      category: 'Settings',
    },

    'settings.inventorywarehouses': {
      route: '/settings/inventorywarehouses',
      title: 'Inventory Warehouses',
      description:
        'Create and configure inventory warehouse locations, bins, shelves, and totes for stock tracking and pick/pack operations. These are inventory storage locations, NOT shipping label return addresses.',
      category: 'Settings',
    },

    'settings.inventorysync': {
      route: '/settings/inventorysync',
      title: 'Inventory Sync',
      description:
        'Configure and connect external inventory sources to ShipStation. Use this when the user wants to add, set up, or configure an inventory source or sync. Also manages automatic inventory sync across stores.',
      category: 'Settings',
    },

    'settings.integrations': {
      route: '/settings/integrations',
      title: 'Integration Partners',
      description: 'Connect and manage third-party integrations',
      category: 'Settings',
    },

    'settings.addonmarketplace': {
      route: '/settings/addonmarketplace',
      title: 'Add-On Marketplace',
      description: 'Browse and activate ShipStation add-ons and extensions',
      category: 'Settings',
    },

    'settings.labs': {
      route: '/settings/labs',
      title: 'Labs',
      description:
        'Enable or disable experimental features in ShipStation Labs. View available labs features, beta features, and experimental functionality that can be toggled on or off.',
      category: 'Settings',
    },

    'settings.trackingpages': {
      route: '/settings/trackingpages',
      title: 'Branded Tracking Pages',
      description: 'Customize branded tracking pages for customers',
      category: 'Settings',
    },

    'settings.rateshopper': {
      route: '/settings/rateShopper',
      title: 'Rate Shopper',
      description: 'Configure service collections and rate shopping preferences',
      category: 'Settings',
    },

    'settings.warehousepicking': {
      route: '/settings/warehousepicking',
      title: 'Warehouse Picking',
      description: 'Configure warehouse picking settings and workflows',
      category: 'Settings',
    },

    'settings.warehousepacking': {
      route: '/settings/warehousepacking',
      title: 'Warehouse Packing',
      description: 'Configure warehouse packing settings and workflows',
      category: 'Settings',
    },

    'settings.refundassist': {
      route: '/settings/refundassist',
      title: 'Refund Assist',
      description: 'Automatically void and refund unused shipping labels. Also known as auto-refund or automatic label refund.',
      category: 'Settings',
    },

    // Order Commands
    'orders.awaiting': {
      route: '/orders/awaiting-shipment',
      title: 'Awaiting Shipment',
      description:
        'View and manage orders that are ready to be shipped. This is the default order view and should be used when a user asks to see orders generally. Use other order commands ONLY if a user asks to see orders in specific statuses like shipped, cancelled, on hold, or awaiting payment.',
      category: 'Orders',
    },

    'orders.shipped': {
      route: '/orders/shipped',
      title: 'Shipped Orders',
      description: 'View shipped order details and order-level information for shipped orders',
      category: 'Orders',
    },

    'orders.onhold': {
      route: '/orders/on-hold',
      title: 'On Hold',
      description: 'Orders that are on hold',
      category: 'Orders',
    },

    'orders.awaitingpayment': {
      route: '/orders/awaiting-payment',
      title: 'Awaiting Payment',
      description:
        'View orders with Awaiting Payment status. Use this when the user asks to see awaiting payment orders — do NOT use order-search for this.',
      category: 'Orders',
    },

    'orders.cancelled': {
      route: '/orders/cancelled',
      title: 'Cancelled Orders',
      description:
        'View cancelled orders. This is the most common cancelled view — use this unless the user specifically mentions 3PL or fulfillment cancellations.',
      category: 'Orders',
    },

    'orders.pendingfulfillment': {
      route: '/orders/pending-fulfillment',
      title: 'Orders Pending Fulfillment',
      description: 'View orders pending fulfillment by a 3PL provider. This is an order status view, NOT the fulfillments grid.',
      category: 'Orders',
    },

    'orders.rejectedfulfillment': {
      route: '/orders/rejected-fulfillment',
      title: 'Orders with Rejected Fulfillment',
      description: 'View orders where fulfillment was rejected by a 3PL provider. This is an order status view, NOT the fulfillments grid.',
      category: 'Orders',
    },

    'orders.alerts': {
      route: '/orders/alerts',
      title: 'Order Alerts',
      description: 'View and manage order alerts and notifications',
      category: 'Orders',
    },

    // Shipment Commands
    'shipments.batches': {
      route: '/shipments/batches',
      title: 'Shipment Batches',
      description:
        'Create and manage shipment batches. Also known as order batches — use this when the user asks about batches, batch processing, or batch shipping.',
      category: 'Shipments',
    },

    'shipments.endofday': {
      route: '/shipments/end-of-day',
      title: 'End of Day',
      description: 'Process end of day shipments and manifests',
      category: 'Shipments',
    },

    'shipments.pickups': {
      route: '/shipments/shipment-pickups',
      title: 'Carrier Pickups',
      description:
        'Schedule and manage carrier pickups. Prefer this command for scheduling pickups with any carrier. Use shipments.uspspickup ONLY for manually scheduling USPS pickups when the user specifically requests that.',
      category: 'Shipments',
    },

    'shipments.uspspickup': {
      route: '/shipments/usps-pickups',
      title: 'USPS Pickup',
      description:
        'Schedule manual USPS package pickups. Use this ONLY if the user requests to manually schedule a USPS pickup. For all other carrier pickups, use shipments.pickups instead.',
      category: 'Shipments',
    },

    'shipments.recent': {
      route: '/shipments/shipped-recent',
      title: 'Recently Shipped',
      description:
        'View shipped label details, tracking numbers, and carrier information for recently shipped shipments. Use this when the user asks about tracking or shipment status. This is the default shipments view to use when users ask to see shipments generally.',
      category: 'Shipments',
    },

    'shipments.intransit': {
      route: '/shipments/shipped-in-transit',
      title: 'In Transit',
      description: 'View shipments currently in transit',
      category: 'Shipments',
    },

    'shipments.delivered': {
      route: '/shipments/shipped-delivered',
      title: 'Delivered',
      description: 'View delivered shipments',
      category: 'Shipments',
    },

    'shipments.deliveryexceptions': {
      route: '/shipments/shipped-delivery-exceptions',
      title: 'Shipment Delivery Exceptions',
      description:
        'View shipments with delivery exceptions — packages that encountered problems during carrier transit (e.g., failed delivery attempts, damaged, returned to sender).',
      category: 'Shipments',
    },

    'shipments.voided': {
      route: '/shipments/shipped-voided',
      title: 'Voided Shipments',
      description: 'View voided shipments and labels',
      category: 'Shipments',
    },

    'shipments.containershipments': {
      route: '/shipments/container-shipments',
      title: 'Container Shipments',
      description: 'Manage container shipment manifests',
      category: 'Shipments',
    },

    // Product Commands
    'products.list': {
      route: '/products',
      title: 'Products & Inventory',
      description: 'Manage products, SKUs, inventory levels, or create new product records',
      category: 'Products',
    },

    'products.inventory': {
      route: '/products/inventory',
      title: 'Inventory Management Grid',
      description: 'View and manage product inventory across warehouses',
      category: 'Products',
    },

    'products.categories': {
      route: '/products/categories',
      title: 'Product Categories',
      description: 'Manage product categories and organization',
      category: 'Products',
    },

    'products.purchaseorders': {
      route: '/products/purchaseorders',
      title: 'Purchase Orders',
      description: 'Create and manage purchase orders for inventory',
      category: 'Products',
    },

    'products.suppliers': {
      route: '/products/suppliers',
      title: 'Suppliers',
      description: 'Manage product suppliers and vendor information',
      category: 'Products',
    },

    'products.externalinventory': {
      route: '/products/externalinv',
      title: 'External Inventory',
      description:
        'View current external inventory quantities. This is a READ-ONLY view of synced stock levels. Do NOT use this when the user wants to configure, add, or set up an inventory source — use settings.inventorysync for that.',
      category: 'Products',
    },

    'products.presetgroups': {
      route: '/products/producttype',
      title: 'Preset Groups',
      description: 'Manage preset groups (product types) to create defaults that can easily be applied to many products',
      category: 'Products',
    },

    // Customer Commands
    'customers.list': {
      route: '/customers',
      title: 'Customers',
      description: 'View customer information and history, and edit or create customer records',
      category: 'Customers',
    },

    // Returns Commands
    'returns.list': {
      route: '/returns',
      title: 'Returns',
      description: 'Manage return shipments and return labels',
      category: 'Returns',
    },

    // Fulfillments Commands (under Shipments)
    'fulfillments.list': {
      route: '/shipments/fulfillments',
      title: 'Fulfillments',
      description: 'View and manage all fulfillment shipments from 3PL providers',
      category: 'Fulfillments',
    },

    'fulfillments.recent': {
      route: '/shipments/fulfillments/recent',
      title: 'Recent Fulfillments',
      description: 'View recently processed fulfillments',
      category: 'Fulfillments',
    },

    'fulfillments.pending': {
      route: '/shipments/fulfillments/pending',
      title: 'Pending Fulfillments',
      description: 'View fulfillments pending shipment',
      category: 'Fulfillments',
    },

    'fulfillments.intransit': {
      route: '/shipments/fulfillments/in-transit',
      title: 'In-Transit Fulfillments',
      description: 'View fulfillments currently in transit',
      category: 'Fulfillments',
    },

    'fulfillments.delivered': {
      route: '/shipments/fulfillments/delivered',
      title: 'Delivered Fulfillments',
      description: 'View delivered fulfillments',
      category: 'Fulfillments',
    },

    'fulfillments.deliveryexception': {
      route: '/shipments/fulfillments/delivery-exception',
      title: 'Fulfillment Delivery Exceptions',
      description:
        'View 3PL fulfillment orders with delivery exceptions. Use this only when the user is specifically asking about third-party fulfillment problems.',
      category: 'Fulfillments',
    },

    'fulfillments.cancelled': {
      route: '/shipments/fulfillments/cancelled',
      title: 'Cancelled Fulfillments',
      description: 'View cancelled fulfillments',
      category: 'Fulfillments',
    },

    // Dashboard & Insights
    'dashboard.insights': {
      route: '/dashboard',
      title: 'Dashboard',
      description:
        'View the main dashboard home page. Use this when the user asks for the dashboard or analytics generally without specifying a specific tab like orders, operations, sales, products, warehouses, or inventory.',
      category: 'Dashboard',
    },

    'dashboard.overview': {
      route: '/dashboard/overview',
      title: 'Order Overview',
      description: 'View order data overview with key order metrics and trends',
      category: 'Dashboard',
    },

    'dashboard.operations': {
      route: '/dashboard/operations',
      title: 'Shipment Operations',
      description: 'View shipment operations performance metrics and carrier analytics',
      category: 'Dashboard',
    },

    'dashboard.sales': {
      route: '/dashboard/sales',
      title: 'Sales & Customers',
      description: 'View sales analytics, customer trends, and revenue data',
      category: 'Dashboard',
    },

    'dashboard.products': {
      route: '/dashboard/products',
      title: 'Product Analytics',
      description: 'View product performance analytics and insights',
      category: 'Dashboard',
    },

    'dashboard.warehouses': {
      route: '/dashboard/warehouses',
      title: 'Warehouse Performance',
      description: 'View warehouse performance metrics and analytics',
      category: 'Dashboard',
    },

    'dashboard.inventory': {
      route: '/dashboard/inventory',
      title: 'Inventory Overview',
      description: 'View inventory analytics and stock level insights',
      category: 'Dashboard',
    },

    'dashboard.reports': {
      route: '/dashboard/reports',
      title: 'Reports',
      description:
        'View and generate downloadable shipping reports including shipment counts, revenue, and cost breakdowns over custom date ranges.',
      category: 'Dashboard',
    },

    // Home & Onboarding
    'home.page': {
      route: '/home',
      title: 'Home',
      description: 'ShipStation home page and quick actions',
      category: 'Navigation',
    },

    'home.setupwizard': {
      route: '/home/setup-wizard',
      title: 'Setup Wizard',
      description:
        'First-time user setup wizard for getting started with ShipStation. Use this for onboarding, new user setup, or when users ask how to get started. Guides users through account setup, connecting stores, configuring carriers, setting up warehouses, and other essential initial configuration steps.',
      category: 'Navigation',
    },

    // Other Pages
    'scan.page': {
      route: '/scan',
      title: 'Barcode Scanner',
      description: 'Scan barcodes for quick order processing',
      category: 'Tools',
    },
  },
};

/**
 * Get command definition by ID
 * @param {string} commandId - Command ID to look up
 * @returns {NavigationCommand|null} Command definition or null
 */
export const getCommand = (commandId) => {
  return navigationManifest.commands[commandId] || null;
};

/**
 * Get all command IDs
 * @returns {string[]} Array of command IDs
 */
export const getAllCommandIds = () => {
  return Object.keys(navigationManifest.commands);
};

/**
 * @typedef {object} BackendNavigationCommand
 * @property {string} title - Display title
 * @property {string} description - Description for semantic matching
 * @property {string} category - Category grouping
 * @property {string[]} [parameters] - Optional route parameters
 */

/**
 * Get navigation manifest optimized for backend (without frontend-specific properties)
 * Strips out 'route' and 'highlightSelector' to reduce token usage
 * @returns {Record<string, BackendNavigationCommand>} Commands without frontend properties
 */
export const getManifestForBackend = () => {
  /** @type {Record<string, BackendNavigationCommand>} */
  const backendCommands = {};

  Object.entries(navigationManifest.commands).forEach(([commandId, command]) => {
    backendCommands[commandId] = {
      title: command.title,
      description: command.description,
      category: command.category,
      parameters: command.parameters,
    };
  });

  return backendCommands;
};
